import PriceContainer from 'components/PriceContainer';
import { TextBody } from 'components/Typography/General';
import React, { useMemo } from 'react';
import { getDirectionByLanguage } from 'utils/get_direction';
import { truncateText } from 'utils/helper';

const CourseData = (props) => {
  const {
    discount,
    price,
    bundleType,
    courseOrganizer = '',
    learningProgram = '',
    categoryName = '',
    subCategoryName = '',
    levelCourse = '',
    delivery = '',
    certificateAwarded = '',
    facilitator = '',
    courseDuration = '',
    timeEstimation = '',
    scheduleTime = new Date(),
    scheduleDate = new Date(),
    timezone = '',
    language = '',
    month = '',
    isMobile = false,
    isBundling = false,
    totalCourseBundling = 0,
    city = '',
    isProduct = false,
    isBundle = false,
    productType = '',
    totalPages = '',
    readingTime = '',
    center = '',
    releaseDate = '',
    publishDate = '',
    referenceNumber = '',
    version = '',
    formatType = '',
    status = '',
    plannedCoursesCount = null,
    plannedCoursesProductsCount = null,
    plannedProductsCount = null,
    newCoursesProductsReleasedMonthly = null,
    newCoursesReleasedMonthly = null,
    newProductsReleasedMonthly = null
  } = props;

  const isBundlingInformation = ['courses-in-the-bundle', 'course-duration', 'certification'];
  const isNotBundlingInformation = [
    'learning-program',
    'main-category',
    'sub-category',
    'course-level',
    'delivery-type',
    'certification',
    'facilitator',
    'course-duration',
    'course-schedule',
    'schedule-time',
    'time-zone',
    'city',
    'language',
  ];

  const isProductInformation = [
    'type',
    'total-pages',
    'reading-time',
    'language',
    'center',
    'release-date',
    'reference',
  ];

  const isBundleInformation = [
    'status',
    'facilitator',
    'format',
    'total-pages',
    'course-duration',
    'time-estimation',
    'language',
    'center',
    'publish-date',
    'reference',
    'certification',
    'version',
    'courses-in-the-bundle',
    'planned-in-the-bundle',
    'release-in-the-bundle',
  ];

  const data = useMemo(
    () => [
      {
        id: 'status',
        title: 'Status',
        data: status,
        showingCondition: !!status,
      },
      {
        id: 'facilitator',
        title: 'Researcher/Facilitator Name',
        data: truncateText(facilitator, 50),
        showingCondition: facilitator !== ' ',
      },
      {
        id: 'format',
        title: 'Available format(s)',
        data: String(formatType).toUpperCase(),
        showingCondition: typeof formatType === 'string' && formatType.length > 0,
      },
      {
        id: 'version',
        title: 'Version',
        data: version,
        showingCondition: !!version,
      },
      {
        id: 'learning-program',
        title: 'Course Type',
        data: learningProgram,
        showingCondition: true,
      },
      {
        id: 'courses-in-the-bundle',
        title: `${bundleType === 0 ? 'Published courses in the bundle' : bundleType === 1 ? 'Published products in the bundle' : 'Published courses/products in the bundle'}`,
        data: totalCourseBundling,
        showingCondition: totalCourseBundling !== null,
      },
      {
        id: 'planned-in-the-bundle',
        title: `${bundleType === 0 ? 'Planned courses in the bundle' : bundleType === 1 ? 'Planned products in the bundle' : 'Planned courses/products in the bundle'}`,
        data: bundleType === 0 ? plannedCoursesCount : bundleType === 1 ? plannedProductsCount : plannedCoursesProductsCount,
        showingCondition: (plannedCoursesCount || plannedProductsCount || plannedCoursesProductsCount),
      },
      {
        id: 'release-in-the-bundle',
        title: `${bundleType === 0 ? 'New courses released monthly' : bundleType === 1 ? 'New products released monthly' : 'New courses/products released monthly'}`,
        data: bundleType === 0 ? newCoursesReleasedMonthly : bundleType === 1 ? newProductsReleasedMonthly : newCoursesProductsReleasedMonthly,
        showingCondition: (newCoursesReleasedMonthly || newProductsReleasedMonthly || newCoursesProductsReleasedMonthly),
      },
      {
        id: 'main-category',
        title: 'Main Category',
        data: categoryName ? categoryName : '-',
        showingCondition: true,
      },
      {
        id: 'sub-category',
        title: 'Sub Category',
        data: subCategoryName ? subCategoryName : '-',
        showingCondition: true,
      },
      {
        id: 'course-level',
        title: 'Course Level',
        data: !levelCourse ? "Beginner" : levelCourse,
        showingCondition: true,
      },
      {
        id: 'delivery-type',
        title: 'Delivery Method',
        data: delivery === "Self Paced" ? "Self-Paced" : delivery,
        showingCondition: true,
      },
      {
        id: 'certification',
        title: 'Certification',
        data: certificateAwarded === true || certificateAwarded === 'true' ? 'Yes' : 'No',
        showingCondition: true,
      },
      {
        id: 'course-duration',
        title: isBundling || isBundle ? 'Total time invested' : 'Course Duration',
        data: courseDuration,
        showingCondition: courseDuration,
      },
      {
        id: 'time-estimation',
        title: 'Time estimation',
        data: timeEstimation,
        showingCondition: timeEstimation,
      },
      {
        id: 'course-schedule',
        title: 'Course Schedule',
        data:
          (scheduleDate?.dateFrom === null && scheduleDate?.dateTo === null) ||
          (scheduleDate?.dateTo === 'null ' && scheduleDate?.dateFrom === 'null')
            ? null
            : `${new Date(scheduleDate?.dateFrom).getDate()} ${
                month[new Date(scheduleDate?.dateFrom).getMonth()]
              } ${new Date(scheduleDate?.dateFrom).getFullYear()} - ${new Date(scheduleDate?.dateTo).getDate()} ${
                month[new Date(scheduleDate?.dateTo).getMonth()]
              } ${new Date(scheduleDate?.dateTo).getFullYear()}`,
        showingCondition: delivery === 'Live Online' || delivery === 'Classroom' || delivery === 'Blended Learning',
      },
      {
        id: 'schedule-time',
        title: 'Schedule Time',
        data: `${scheduleTime?.timeFrom} - ${scheduleTime?.timeTo}`,
        showingCondition: delivery === 'Live Online' || delivery === 'Classroom' || delivery === 'Blended Learning',
      },
      {
        id: 'time-zone',
        title: 'Time Zone',
        data: timezone,
        showingCondition:
          (delivery === 'Live Online' || delivery === 'Classroom' || delivery === 'Blended Learning') &&
          timezone !== null &&
          timezone !== 'null',
      },
      {
        id: 'city',
        title: 'City',
        data: city,
        showingCondition: delivery === 'Classroom' && city !== null && city !== 'null',
      },
      {
        id: 'language',
        title: 'Language',
        data: `${language && language[0].toUpperCase()}${language.slice(1)}`,
        showingCondition: language,
      },
      {
        id: 'type',
        title: 'Product Type',
        data: productType,
        showingCondition: productType,
      },
      {
        id: 'total-pages',
        title: 'Number of pages',
        data: totalPages,
        showingCondition: totalPages && totalPages > 0,
      },
      {
        id: 'reading-time',
        title: 'Reading Time',
        data: readingTime,
        showingCondition: readingTime,
      },
      {
        id: 'center',
        title: 'Center',
        data: truncateText(center),
        showingCondition: center,
      },
      {
        id: 'release-date',
        title: 'Release Date',
        data: releaseDate,
        showingCondition: releaseDate,
      },
      {
        id: 'reference',
        title: 'Reference Number',
        data: referenceNumber,
        showingCondition: referenceNumber,
      },
      {
        id: 'publish-date',
        title: 'Published Date',
        data: publishDate,
        showingCondition: publishDate,
      },
    ],
    [
      categoryName,
      center,
      certificateAwarded,
      city,
      courseDuration,
      delivery,
      facilitator,
      formatType,
      isBundle,
      isBundling,
      language,
      learningProgram,
      levelCourse,
      month,
      productType,
      publishDate,
      readingTime,
      referenceNumber,
      releaseDate,
      scheduleDate?.dateFrom,
      scheduleDate?.dateTo,
      scheduleTime?.timeFrom,
      scheduleTime?.timeTo,
      status,
      subCategoryName,
      timeEstimation,
      timezone,
      totalCourseBundling,
      totalPages,
      version,
    ]
  );

  const displayedInformation = useMemo(() => {
    const informationMapping = [
      { isDisplayed: isBundle, data: isBundleInformation },
      { isDisplayed: isProduct, data: isProductInformation },
      { isDisplayed: isBundling, data: isBundlingInformation },
    ];

    const information = informationMapping.filter((item) => item.isDisplayed)[0]?.data ?? isNotBundlingInformation;

    return data.filter((item) => information.includes(item.id) && item.showingCondition);
  }, [
    data,
    isBundle,
    isBundleInformation,
    isBundling,
    isBundlingInformation,
    isNotBundlingInformation,
    isProduct,
    isProductInformation,
  ]);

  return (
    <>
      <div style={{ display: 'flex', marginTop: 10 }}>
        <PriceContainer
          variableDiscount={[parseFloat(discount), parseFloat(discount?.['$numberDecimal'])]}
          variablePrice={[parseFloat(price), parseFloat(price?.['$numberDecimal'])]}
          courseOrganizer={courseOrganizer}
          isLarge
        />
      </div>
      <div className={`description-checkout__details ${isMobile ? 'pb-3' : ''}`}>
        {displayedInformation.map((courseData) => {
          return (
            <TextBody tag="div" variety={isMobile ? 3 : 5} className="informations" key={courseData.id}>
              <div className="light">{courseData.title}:</div>
              <div dir={getDirectionByLanguage(language)} className="bold">
                {courseData.data}
              </div>
            </TextBody>
          );
        })}
      </div>
    </>
  );
};

export default CourseData;
